'use client'

import { useEffect } from 'react'
import { FaTimes } from 'react-icons/fa'
import { BsPersonBoundingBox } from 'react-icons/bs'
import { useTranslation } from 'react-i18next'
import Portal from './Portal'

// Lightweight picker for the AI Character face-swap reference. Shows the user's
// recently used characters (click to re-apply) plus a short note on how to add
// more. The built-in sample characters live on the Inputs panel, not here.
const CharacterModal = ({
  isOpen,
  closeModal,
  recentCharacters = [],
  charImage,
  setCharImage,
  onViewExplore,
  onViewProfile,
}) => {
  const { t } = useTranslation()

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'unset'
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  if (!isOpen) return null

  const applyCharacter = (url) => {
    setCharImage(url)
    closeModal()
  }

  return (
    <Portal>
      <div
        onClick={(e) => {
          if (e.target === e.currentTarget) closeModal()
        }}
        className="fixed z-[110] top-0 left-0 w-full h-full bg-black/60 flex justify-center items-center transition-opacity duration-200"
      >
        <div className="bg-[var(--middle-bg-color)] m-4 w-full max-w-lg max-h-[85vh] flex flex-col rounded-2xl shadow-2xl p-5 text-gray-100 relative">
          <header className="flex items-start pr-10">
            <h2 className="text-xl font-semibold text-white text-shadow">
              {t('aiCharacter')}
            </h2>
            <button
              type="button"
              onClick={closeModal}
              aria-label="Close modal"
              className="absolute right-4 top-4 text-white hover:text-gray-300"
              style={{ fontSize: '1.5rem' }}
            >
              <FaTimes />
            </button>
          </header>

          <main className="mt-4 flex-1 min-h-0 overflow-y-auto space-y-5">
            <section>
              <h3 className="text-sm font-semibold text-white mb-2">
                Recent characters
              </h3>
              {recentCharacters.length > 0 ? (
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                  {recentCharacters.map((url) => {
                    const active = charImage === url
                    return (
                      <button
                        type="button"
                        key={url}
                        onClick={() => applyCharacter(url)}
                        title="Use this character"
                        className={`relative aspect-square rounded-xl overflow-hidden border transition-colors ${
                          active
                            ? 'border-[var(--main-bg-color)] ring-2 ring-[var(--main-bg-color)]'
                            : 'border-[var(--foreground-bg-color)] hover:border-gray-400'
                        }`}
                      >
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                          src={url}
                          alt="Recent character"
                          className="w-full h-full object-cover"
                        />
                      </button>
                    )
                  })}
                </div>
              ) : (
                <p className="text-sm text-gray-400">
                  Characters you use will show up here for quick reuse.
                </p>
              )}
            </section>

            <section className="rounded-xl border border-white/10 bg-black/20 p-4">
              <h3 className="text-sm font-semibold text-white mb-1">
                Add a character
              </h3>
              <p className="text-sm text-gray-300 leading-relaxed">
                Open any image on Explore, your Profile, or Create and press{' '}
                <span className="inline-flex items-center gap-1 align-middle font-semibold text-white">
                  <BsPersonBoundingBox
                    size={12}
                    className="relative top-[1px]"
                  />
                  <span>Use Character</span>
                </span>
                .
              </p>
            </section>
          </main>

          <footer className="shrink-0 mt-4 flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={onViewExplore}
              className="bg-[var(--main-bg-color)] text-white rounded-full py-2 px-4 w-full font-semibold transition-colors"
            >
              {t('viewExplore')}
            </button>
            <button
              type="button"
              onClick={onViewProfile}
              className="bg-[var(--foreground-bg-color)] text-white rounded-full py-2 px-4 w-full font-semibold transition-colors"
            >
              {t('viewProfile')}
            </button>
          </footer>
        </div>
      </div>
    </Portal>
  )
}

export default CharacterModal
